/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseMutationResult,
  UseQueryResult
} from "react-query";
import { useDispatch } from "react-redux";
import {
  getGlassOrders,
  getGlassOrder,
  postGlassOrder,
  patchGlassOrder,
  updateGlassOrderStatus,
  deleteGlassOrder
} from "../../api/glassOrder";
import { GlassOrder, GlassOrderFormData, GetGlassOrdersParams } from "../../interfaces/GlassOrder";
import { ApiResponse } from "../../interfaces/Api";
import { notificationAdd } from "../../actions/notification";
import useCurrentResourceCentre from "../useCurrentResourceCentre";

export const glassOrdersQueryKey = "glassOrders";
export const glassOrderQueryKey = "glassOrder";

// get glass orders (list)
export function useGlassOrders(
  params?: GetGlassOrdersParams,
  options?: { enabled?: boolean }
): UseQueryResult<ApiResponse<{ results: GlassOrder[]; total: number }>, Error> {
  const resourceCentre = useCurrentResourceCentre();

  return useQuery([glassOrdersQueryKey, resourceCentre?.id, params], () => getGlassOrders(params), {
    enabled: (options?.enabled ?? true) && !!resourceCentre?.id,
    keepPreviousData: true
  });
}

// get single glass order by ID
export function useGetGlassOrder(
  id?: number | string | null,
  options?: { enabled?: boolean }
): UseQueryResult<ApiResponse<GlassOrder>, Error> {
  const resourceCentre = useCurrentResourceCentre();

  return useQuery(
    [glassOrderQueryKey, resourceCentre?.id, id],
    () => getGlassOrder(id as number | string),
    {
      enabled: (options?.enabled ?? true) && !!resourceCentre?.id && !!id
    }
  );
}

// post glass order (create)
export function usePostGlassOrder(): UseMutationResult<
  ApiResponse<GlassOrder>,
  Error,
  Partial<GlassOrderFormData>
> {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation((data: Partial<GlassOrderFormData>) => postGlassOrder(data), {
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries(glassOrdersQueryKey);
        dispatch(
          notificationAdd({
            id: Date.now(),
            autoTimeout: true,
            message: "Glass order created successfully",
            variant: "success"
          })
        );
      }
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || error?.data?.message || "Failed to create glass order",
          variant: "error"
        })
      );
    }
  });
}

// patch glass order (edit)
export function usePatchGlassOrder(): UseMutationResult<
  ApiResponse<GlassOrder>,
  Error,
  { id: number | string; data: Partial<GlassOrderFormData> }
> {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation(
    ({ id, data }: { id: number | string; data: Partial<GlassOrderFormData> }) =>
      patchGlassOrder(id, data),
    {
      onSuccess: (response) => {
        if (response.success) {
          queryClient.invalidateQueries(glassOrdersQueryKey);
          queryClient.invalidateQueries(glassOrderQueryKey);
          dispatch(
            notificationAdd({
              id: Date.now(),
              autoTimeout: true,
              message: "Glass order updated successfully",
              variant: "success"
            })
          );
        }
      },
      onError: (error: any) => {
        dispatch(
          notificationAdd({
            id: Date.now(),
            autoTimeout: true,
            message: error?.message || error?.data?.message || "Failed to update glass order",
            variant: "error"
          })
        );
      }
    }
  );
}

// patch glass order status
export function useUpdateGlassOrderStatus(): UseMutationResult<
  ApiResponse<GlassOrder>,
  Error,
  { id: number | string; payload: { status?: string; deliveryDate?: string | null } }
> {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation(
    ({
      id,
      payload
    }: {
      id: number | string;
      payload: { status?: string; deliveryDate?: string | null };
    }) => updateGlassOrderStatus(id, payload),
    {
      onSuccess: (response) => {
        if (response.success) {
          queryClient.invalidateQueries(glassOrdersQueryKey);
          queryClient.invalidateQueries(glassOrderQueryKey);
          dispatch(
            notificationAdd({
              id: Date.now(),
              autoTimeout: true,
              message: "Glass order status updated successfully",
              variant: "success"
            })
          );
        }
      },
      onError: (error: any) => {
        dispatch(
          notificationAdd({
            id: Date.now(),
            autoTimeout: true,
            message:
              error?.message || error?.data?.message || "Failed to update glass order status",
            variant: "error"
          })
        );
      }
    }
  );
}

// delete glass order
export function useDeleteGlassOrder(): UseMutationResult<
  ApiResponse<null>,
  Error,
  number | string
> {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation((id: number | string) => deleteGlassOrder(id), {
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries(glassOrdersQueryKey);
        dispatch(
          notificationAdd({
            id: Date.now(),
            autoTimeout: true,
            message: "Glass order deleted successfully",
            variant: "success"
          })
        );
      }
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || error?.data?.message || "Failed to delete glass order",
          variant: "error"
        })
      );
    }
  });
}
